import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export type UploadedFile = {
  name: string;
  size: number;
  type: string;
  path: string;
};

export function useDocumentList(taxReturnId: string) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const { toast } = useToast();

  const fetchDocuments = async () => {
    try {
      const { data: documents, error } = await supabase
        .from('tax_documents')
        .select('*')
        .eq('tax_return_id', taxReturnId);

      if (error) throw error;

      if (documents) {
        setFiles(documents.map(doc => ({
          name: doc.file_name,
          size: doc.file_size,
          type: doc.file_type,
          path: doc.file_path,
        })));
      }
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      toast({
        title: "Error",
        description: "Failed to fetch documents",
        variant: "destructive",
      });
    }
  };

  const removeDocument = async (filePath: string) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('tax_documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('tax_documents')
        .delete()
        .eq('file_path', filePath);

      if (dbError) throw dbError;

      setFiles(prev => prev.filter(file => file.path !== filePath));
      toast({
        title: "Success",
        description: "Document removed successfully",
      });
    } catch (error: any) {
      console.error('Error removing document:', error);
      toast({
        title: "Error",
        description: "Failed to remove document",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (taxReturnId) {
      fetchDocuments();
    }
  }, [taxReturnId]);

  return {
    files,
    removeDocument,
    refreshFiles: fetchDocuments,
  };
}